import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import type { Point } from '../../doc/geometry';
import type { SheetItem } from '../../doc/schema';
import { useBoard, usePane, usePaneStore, useReadOnly } from '../context';
import { itemLocalToWorld, worldToItemLocal } from '../cutout';
import { flash } from '../flash';
import { floodFill, isStrokeBrush, pickColor, StrokeSession, type BrushParams } from '../paint/brushes';
import { rasterStore, type LiveLayer } from '../paint/rasterStore';
import { enterPaint, isSheet } from '../paint/session';
import { setToolSettings, toolSettings, useToolSettings } from '../toolSettings';
import { screenToWorld, worldToScreen } from './camera';
import { isRejectedTouch } from './DrawOverlay';

/**
 * Screen-space canvas for painting on sketch sheets (the sheet tool in paint mode). A press on
 * the sheet being painted starts a stroke on its active layer (or a lasso loop, or a move of the
 * selected pixels); a press on another sheet makes that one the target; a press off any sheet
 * falls through to the viewport and pans, like the draw overlay. The brush outline follows the
 * pointer, and the pixel selection is outlined with marching ants.
 */
export function PaintOverlay() {
  const active = usePane((s) => s.tool === 'sheet' && s.paintSheetId !== null);
  const readOnly = useReadOnly();
  if (!active || readOnly) return null;
  return <PaintCanvas />;
}

/** How often a resting spray can puffs, and the marching ants step (ms). */
const SPRAY_DWELL_MS = 40;
const ANTS_STEP_MS = 140;

type Gesture =
  | {
      kind: 'stroke';
      pointerId: number;
      live: LiveLayer;
      session: StrokeSession;
      /** The sheet's geometry at the press (the gesture keeps mapping to it). */
      sheet: SheetItem;
      dwell: ReturnType<typeof setInterval> | null;
    }
  | { kind: 'lasso'; pointerId: number; sheet: SheetItem; points: number[]; minStep: number }
  | { kind: 'move'; pointerId: number; sheet: SheetItem; start: Point; x: number; y: number };

function PaintCanvas() {
  const board = useBoard();
  const pane = usePaneStore();
  const size = usePane((s) => s.size);
  const brush = useToolSettings((s) => s.brush);
  const store = rasterStore(board);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gesture = useRef<Gesture | null>(null);
  const liveTouch = useRef(false);
  const hover = useRef<Point | null>(null);
  const ants = useRef(0);
  const dpr = window.devicePixelRatio || 1;

  /** Drops a stroke in progress and puts the layer back as it was. */
  const abandonStroke = (g: Extract<Gesture, { kind: 'stroke' }>) => {
    if (g.dwell) clearInterval(g.dwell);
    g.session.cancel();
    store.cancelStroke(g.live);
    if (g.live.scratch) g.live.scratch.style.opacity = '';
  };

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = Math.round(size.w * dpr);
    c.height = Math.round(size.h * dpr);
    render();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, dpr]);

  // Safari runs its touch defaults for pointers we ignore; cancel them at the touch level.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const swallow = (e: TouchEvent) => {
      if (e.cancelable) e.preventDefault();
    };
    const opts: AddEventListenerOptions = { passive: false };
    c.addEventListener('touchstart', swallow, opts);
    c.addEventListener('touchmove', swallow, opts);
    c.addEventListener('touchend', swallow, opts);
    return () => {
      c.removeEventListener('touchstart', swallow);
      c.removeEventListener('touchmove', swallow);
      c.removeEventListener('touchend', swallow);
    };
  }, []);

  // the brush outline and the selection are in screen space: redraw when the camera, the brush
  // or the selection changes, and keep the ants marching while there is a selection
  useEffect(() => {
    const a = pane.subscribe((s, prev) => {
      if (s.camera !== prev.camera) render();
    });
    const b = toolSettings.subscribe(() => render());
    const c = store.subscribe(() => render());
    const march = setInterval(() => {
      const sheetId = pane.getState().paintSheetId;
      if (!sheetId || (!store.selection(sheetId) && !store.floating())) return;
      ants.current = (ants.current + 1) % 8;
      render();
    }, ANTS_STEP_MS);
    return () => {
      a();
      b();
      c();
      clearInterval(march);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pane, store]);

  // leaving paint mode in the middle of a gesture: nothing half done stays behind
  useEffect(
    () => () => {
      const g = gesture.current;
      gesture.current = null;
      if (g?.kind === 'stroke') abandonStroke(g);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const toLocal = (e: { clientX: number; clientY: number }): Point => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  /** The sheet under a world point: the one being painted first, else the topmost visible one. */
  const sheetAt = (world: Point): SheetItem | null => {
    const s = pane.getState();
    const current = s.paintSheetId ? board.index.get(s.paintSheetId) : undefined;
    if (isSheet(current) && contains(current, world)) return current;
    const hits = board.index.query({ x: world.x - 0.5, y: world.y - 0.5, w: 1, h: 1 });
    for (let i = hits.length - 1; i >= 0; i--) {
      const it = hits[i]!;
      if (isSheet(it) && !board.isHidden(it) && !board.isLocked(it) && contains(it, world)) return it;
    }
    return null;
  };

  /** World point to a layer pixel of the sheet, and a layer pixel to the screen. */
  const toPixel = (sheet: SheetItem, world: Point): Point => {
    const l = worldToItemLocal(sheet, world);
    return { x: (l.x * sheet.props.pxW) / Math.max(1, sheet.w), y: (l.y * sheet.props.pxH) / Math.max(1, sheet.h) };
  };

  const toScreen = (sheet: SheetItem, x: number, y: number): Point =>
    worldToScreen(
      pane.getState().camera,
      itemLocalToWorld(sheet, { x: (x * sheet.w) / Math.max(1, sheet.props.pxW), y: (y * sheet.h) / Math.max(1, sheet.props.pxH) }),
    );

  /** A closed (or still open) outline in layer pixels, as marching ants. */
  const drawOutline = (ctx: CanvasRenderingContext2D, sheet: SheetItem, points: number[], closed: boolean) => {
    if (points.length < 4) return;
    ctx.beginPath();
    for (let i = 0; i + 1 < points.length; i += 2) {
      const p = toScreen(sheet, points[i]!, points[i + 1]!);
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    }
    if (closed) ctx.closePath();
    ctx.lineWidth = 1;
    ctx.setLineDash([]);
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();
    ctx.setLineDash([4, 4]);
    ctx.lineDashOffset = -ants.current;
    ctx.strokeStyle = '#111111';
    ctx.stroke();
    ctx.setLineDash([]);
  };

  const render = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, c.width / dpr, c.height / dpr);
    const s = pane.getState();
    const sheet = s.paintSheetId ? board.index.get(s.paintSheetId) : undefined;
    if (!isSheet(sheet)) return;

    const g = gesture.current;
    const sel = store.selection(sheet.id);
    const float = store.floating();
    if (sel) drawOutline(ctx, sheet, sel.points, true);
    else if (float && float.live.sheetId === sheet.id) {
      // a whole layer on the move: the box around its pixels
      const { x, y } = float;
      const w = float.canvas.width;
      const h = float.canvas.height;
      drawOutline(ctx, sheet, [x, y, x + w, y, x + w, y + h, x, y + h], true);
    }
    if (g?.kind === 'lasso') drawOutline(ctx, sheet, g.points, false);

    const h = hover.current;
    if (!h) return;
    const ts = toolSettings.getState();
    if (ts.brush === 'lasso' || ts.brush === 'move') return; // the cursor says it all
    if (!isStrokeBrush(ts.brush)) {
      const accent = getComputedStyle(c).getPropertyValue('--accent').trim() || '#2b5bd7';
      ctx.strokeStyle = accent;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(h.x - 8, h.y);
      ctx.lineTo(h.x + 8, h.y);
      ctx.moveTo(h.x, h.y - 8);
      ctx.lineTo(h.x, h.y + 8);
      ctx.stroke();
      return;
    }
    const b = ts.brushes[ts.brush];
    const r = ((b.size / 2) * sheet.w * s.camera.zoom) / Math.max(1, sheet.props.pxW);
    ctx.beginPath();
    ctx.arc(h.x, h.y, Math.max(1.5, r), 0, Math.PI * 2);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2.5;
    ctx.globalAlpha = 0.6;
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = '#111111';
    ctx.lineWidth = 1;
    ctx.stroke();
  };

  const pressureOf = (e: { pointerType: string; pressure: number }): number => (e.pointerType === 'pen' ? Math.max(0.05, e.pressure) : 1);

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const g = gesture.current;
    if (g) {
      // a second finger beside a working finger is a gesture (pinch, two-finger tap), not paint
      if (e.pointerType === 'touch' && liveTouch.current) {
        gesture.current = null;
        if (e.currentTarget.hasPointerCapture(g.pointerId)) e.currentTarget.releasePointerCapture(g.pointerId);
        if (g.kind === 'stroke') abandonStroke(g);
        render();
      }
      return;
    }
    const s = pane.getState();
    if (e.button !== 0 && e.pointerType !== 'touch') return; // middle/right: fall through (pan)
    if (s.spaceHeld) return;
    if (isRejectedTouch(e.pointerType)) return;
    const local = toLocal(e);
    const world = screenToWorld(s.camera, local);
    const sheet = sheetAt(world);
    if (!sheet) return; // off every sheet: the viewport pans
    e.stopPropagation();
    const el = e.currentTarget;
    if (sheet.id !== s.paintSheetId) {
      // switch to this sheet; its layer loads and the next press paints
      enterPaint({ board, pane }, sheet.id);
      flash(el, `Painting on this sheet`);
      return;
    }
    const ts = toolSettings.getState();
    const kind = e.altKey && ts.brush !== 'lasso' && ts.brush !== 'move' ? 'picker' : ts.brush;
    const px = toPixel(sheet, world);
    liveTouch.current = e.pointerType === 'touch';
    if (kind === 'lasso') {
      // an outline needs no pixels: any layer will do, locked or not
      store.dropFloat();
      el.setPointerCapture(e.pointerId);
      const scale = (sheet.w * s.camera.zoom) / Math.max(1, sheet.props.pxW);
      gesture.current = { kind: 'lasso', pointerId: e.pointerId, sheet, points: [px.x, px.y], minStep: Math.max(0.5, 1.5 / Math.max(1e-6, scale)) };
      render();
      return;
    }
    const layerId = s.paintLayerId;
    const def = layerId ? sheet.props.layers[layerId] : undefined;
    const live = store.active(sheet.id);
    if (!layerId || !def || !live || live.layerId !== layerId) {
      flash(el, 'Loading the layer…');
      return;
    }
    if (live.unavailable) return flash(el, 'This layer is not available offline yet');
    if (!live.loaded) return flash(el, 'Loading the layer…');
    if (kind === 'picker') {
      const color = pickColor(live.canvas, px.x, px.y) ?? sheet.props.paper;
      if (color) {
        setToolSettings({ paintColor: color });
        flash(el, `Colour ${color}`);
      } else flash(el, 'Nothing to pick here');
      return;
    }
    if (def.locked) return flash(el, 'The layer is locked');
    if (!def.visible) return flash(el, 'The layer is hidden');
    if (kind === 'move') {
      if (!store.lift(live)) return flash(el, 'Nothing to move on this layer');
      const f = store.floating()!;
      el.setPointerCapture(e.pointerId);
      gesture.current = { kind: 'move', pointerId: e.pointerId, sheet, start: px, x: f.x, y: f.y };
      render();
      return;
    }
    if (kind === 'fill') {
      const backup = store.beginStroke(live);
      const rect = floodFill(live.canvas, px.x, px.y, ts.paintColor, ts.brushes.fill.opacity, ts.fillTolerance, backup, store.fillRegion(sheet.id));
      store.endStroke(live, rect);
      if (!rect && store.selection(sheet.id)) flash(el, 'Outside the selection');
      return;
    }
    if (!live.scratch) return;
    el.setPointerCapture(e.pointerId);
    const params: BrushParams = { ...ts.brushes[kind], kind, color: ts.paintColor };
    // beginStroke first: it lands pixels the move tool left floating, which the scratch canvas shows
    const backup = store.beginStroke(live);
    const session = new StrokeSession(live.canvas, live.scratch, params, backup, store.strokeClip(sheet.id));
    live.scratch.style.opacity = String(params.opacity);
    session.begin({ x: px.x, y: px.y, pressure: pressureOf(e) });
    const dwell = kind === 'spray' ? setInterval(() => session.dwell(), SPRAY_DWELL_MS) : null;
    gesture.current = { kind: 'stroke', pointerId: e.pointerId, live, session, sheet, dwell };
    hover.current = local;
    render();
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const g = gesture.current;
    const s = pane.getState();
    if (!g) {
      if (e.pointerType === 'touch') return;
      hover.current = toLocal(e);
      render();
      return;
    }
    if (g.pointerId !== e.pointerId) return;
    const native = e.nativeEvent;
    const events = typeof native.getCoalescedEvents === 'function' ? native.getCoalescedEvents() : [];
    const list = events.length ? events : [native];
    if (g.kind === 'move') {
      const px = toPixel(g.sheet, screenToWorld(s.camera, toLocal(e)));
      let dx = px.x - g.start.x;
      let dy = px.y - g.start.y;
      // Shift keeps the move on one axis
      if (e.shiftKey) {
        if (Math.abs(dx) >= Math.abs(dy)) dy = 0;
        else dx = 0;
      }
      store.moveFloat(g.x + dx, g.y + dy);
    } else {
      for (const ev of list) {
        const px = toPixel(g.sheet, screenToWorld(s.camera, toLocal(ev)));
        if (g.kind === 'stroke') g.session.extend({ x: px.x, y: px.y, pressure: pressureOf(ev) });
        else {
          const n = g.points.length;
          if (Math.hypot(px.x - g.points[n - 2]!, px.y - g.points[n - 1]!) >= g.minStep) g.points.push(px.x, px.y);
        }
      }
    }
    hover.current = toLocal(e);
    render();
  };

  const finish = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const g = gesture.current;
    if (!g || g.pointerId !== e.pointerId) return;
    gesture.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    if (g.kind === 'stroke') {
      if (g.dwell) clearInterval(g.dwell);
      const rect = g.session.end();
      if (g.live.scratch) g.live.scratch.style.opacity = '';
      store.endStroke(g.live, rect);
    } else if (g.kind === 'lasso' && e.type !== 'pointercancel') {
      // a loop selects what it holds; a mere tap selects nothing
      const loop = g.points.length >= 6 && spanOf(g.points) >= Math.max(2, g.minStep * 3);
      store.setSelection(g.sheet.id, loop ? g.points : null, toolSettings.getState().brushes.lasso.aliased);
    }
    // a move leaves its pixels floating: the next drag carries on, anything else lands them
    if (e.pointerType === 'touch') hover.current = null;
    render();
  };

  return (
    <canvas
      ref={canvasRef}
      className={`draw-overlay paint-overlay brush-${brush}`}
      style={{ width: size.w, height: size.h }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
      onPointerCancel={finish}
      onPointerLeave={() => {
        if (!gesture.current) {
          hover.current = null;
          render();
        }
      }}
    />
  );
}

function contains(sheet: SheetItem, world: Point): boolean {
  const l = worldToItemLocal(sheet, world);
  return l.x >= 0 && l.y >= 0 && l.x <= sheet.w && l.y <= sheet.h;
}

/** The larger side of the box around a flat point list. */
function spanOf(points: number[]): number {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (let i = 0; i + 1 < points.length; i += 2) {
    x0 = Math.min(x0, points[i]!);
    x1 = Math.max(x1, points[i]!);
    y0 = Math.min(y0, points[i + 1]!);
    y1 = Math.max(y1, points[i + 1]!);
  }
  return Math.max(x1 - x0, y1 - y0);
}
